/* Book and catalog preview viewer, <book-viewer> (snippets/book-viewer.liquid). Issue #95.
   Flipbook (StPageFlip, vendored as assets/page-flip.js and loaded on first open) or scroll; phones use scroll.
   Zoom: buttons, + / - keys, trackpad pinch (ctrl + wheel), Safari gestures, two-finger touch pinch.
   While zoomed, drag or two-finger swipe pans; page turns stay on the arrows, keyboard and scrubber.
   Data is validated here again (defense in depth): only https/CDN page images, only http(s)/mailto links,
   in-range page targets, clamped link boxes; everything is built with DOM APIs, never with HTML strings. */
(() => {
  if (customElements.get('book-viewer')) return;

  const ZOOMS = [1, 1.5, 2, 3];
  const MIN_Z = ZOOMS[0];
  const MAX_Z = ZOOMS[ZOOMS.length - 1];
  const PHONE = window.matchMedia('(max-width: 749px)');
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  const store = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };
  let libPromise = null;

  function loadLibrary(src) {
    if (window.St && window.St.PageFlip) return Promise.resolve();
    if (!libPromise) {
      libPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = () => (window.St && window.St.PageFlip ? resolve() : reject(new Error('page-flip missing')));
        s.onerror = () => { libPromise = null; reject(new Error('page-flip failed to load')); };
        document.head.appendChild(s);
      });
    }
    return libPromise;
  }

  // Page images must come from Shopify's image CDN: cdn.shopify.com, or the /cdn/shop/ path of this store's own
  // domain or its *.myshopify.com domain (what Liquid's image_url produces).
  function safeImage(url) {
    if (typeof url !== 'string') return null;
    try {
      const u = new URL(url, window.location.href);
      if (u.protocol !== 'https:' && u.protocol !== window.location.protocol) return null;
      if (u.username || u.password) return null;
      if (u.hostname === 'cdn.shopify.com') return u.href;
      const storeHost = u.host === window.location.host || /^[a-z0-9-]+\.myshopify\.com$/.test(u.hostname);
      if (storeHost && u.pathname.startsWith('/cdn/shop/')) return u.href;
    } catch (e) { /* invalid URL */ }
    return null;
  }
  function safeLink(url) {
    return typeof url === 'string' && /^(https?:\/\/|mailto:)/i.test(url.trim()) && url.length <= 2000 ? url.trim() : null;
  }
  function parseJSON(text, fallback) {
    try { return JSON.parse(text || ''); } catch (e) { return fallback; }
  }
  function cleanLinks(raw, total) {
    const out = [];
    for (let i = 0; i < total; i++) {
      const page = Array.isArray(raw) && Array.isArray(raw[i]) ? raw[i] : [];
      out.push(page.map((l) => {
        if (!l || !Array.isArray(l.b) || l.b.length !== 4 || !l.b.every(Number.isFinite)) return null;
        const b = l.b.map((v) => Math.min(Math.max(v, 0), 100));
        if (l.u !== undefined) { const u = safeLink(l.u); return u ? { b, u } : null; }
        if (Number.isInteger(l.p) && l.p >= 0 && l.p < total) return { b, p: l.p };
        return null;
      }).filter(Boolean));
    }
    return out;
  }

  class BookViewer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      // Pages and their links are filtered together, so a rejected page can't shift later pages' links.
      const rawPages = parseJSON(this.dataset.pages, []), rawLinks = parseJSON(this.dataset.links, []);
      const kept = [];
      (Array.isArray(rawPages) ? rawPages : []).forEach((p, i) => {
        const s = safeImage(p && p.s), h = safeImage(p && p.h);
        if (s && h) kept.push({ page: { s, h }, links: Array.isArray(rawLinks) ? rawLinks[i] : null, from: i });
      });
      this.pages = kept.map((k) => k.page);
      this.total = this.pages.length;
      if (!this.total) return;                                   // nothing valid to show: keep the button disabled
      // Internal links point at original page numbers; re-map them to the kept pages (and drop ones that left).
      const where = new Map(kept.map((k, i) => [k.from, i]));
      const remapped = kept.map((k) => (Array.isArray(k.links) ? k.links : []).map((l) => (
        l && Number.isInteger(l.p) ? (where.has(l.p) ? Object.assign({}, l, { p: where.get(l.p) }) : null) : l)).filter(Boolean));
      this.links = cleanLinks(remapped, this.total);
      this.ratio = Number(this.dataset.ratio) > 0 ? Number(this.dataset.ratio) : 0.7727;
      this.dlg = this.querySelector('dialog');
      this.wrap = this.querySelector('.bv-wrap');
      this.ref = (name) => this.querySelector(`[data-ref="${name}"]`);
      this.act = (name) => this.querySelector(`[data-act="${name}"]`);
      this.state = { mode: 'flip', zoom: 1, current: 0, built: false, book: null, zoomFrame: 0 };
      this.sound = store.get('ci-viewer-sound') !== 'off';
      this.ref('range').max = String(this.total);
      this.opener = this.querySelector('.bv-open');
      this.opener.disabled = false;
      this.opener.addEventListener('click', () => this.open());
      // Intent to open (hover, focus, touch): start fetching the cover and the flipbook code in the background.
      for (const type of ['pointerenter', 'focus', 'touchstart']) this.opener.addEventListener(type, () => this.prime(), { once: true, passive: true });
      this.wireControls();
      this.ready = true;
    }

    /* ---------------------------------------------------------------- open / close / full screen */
    // The page width the flipbook will show, times the screen's pixel density, in 200px steps (600 to 1000).
    // Page images are requested at that width instead of always 1000px: about half the data on standard screens.
    sdWidth() {
      if (this.state.sdWidth) return this.state.sdWidth;
      const vw = window.innerWidth, vh = window.innerHeight, dpr = Math.min(window.devicePixelRatio || 1, 3);
      const single = vw / Math.max(vh, 1) < this.ratio * 1.45;
      const pageCss = single ? Math.min(vw - 128, (vh - 160) * this.ratio) : Math.min(vw - 128, 1100) / 2;
      this.state.sdWidth = Math.max(600, Math.min(1000, Math.ceil((pageCss * dpr) / 200) * 200));
      return this.state.sdWidth;
    }
    sized(url) {
      return PHONE.matches ? url : url.replace(/([?&])width=\d+/, `$1width=${this.sdWidth()}`);
    }
    prime() {
      if (this.primed || PHONE.matches) return;
      this.primed = true;
      loadLibrary(this.dataset.lib).catch(() => {});
      const pre = new Image(); pre.fetchPriority = 'high'; pre.src = this.sized(this.pages[0].s);
      this.primeImg = pre;                                                   // keep a reference until it lands
    }

    async open() {
      this.prime();
      this.dlg.showModal();
      document.documentElement.style.overflow = 'hidden';
      this.renderSound();
      if (!this.state.built) {
        this.state.built = true;
        try {
          if (!PHONE.matches) await loadLibrary(this.dataset.lib);
          this.ref('status').hidden = true;
        } catch (e) {
          this.ref('status').textContent = 'Flipbook unavailable; showing pages in scroll view.';
        }
      }
      this.setMode(store.get('ci-viewer-mode') || 'flip');
    }

    fsElement() { return document.fullscreenElement || document.webkitFullscreenElement; }

    setMax(on) {
      this.dlg.classList.toggle('is-max', on);
      setTimeout(() => this.layout(), 120);
      const b = this.act('fullscreen');
      b.setAttribute('aria-pressed', String(on));
      b.setAttribute('aria-label', on ? 'Exit full screen' : 'Full screen');
      if (this.state.book) requestAnimationFrame(() => this.state.book.update());
    }
    enterFull() {
      this.setMax(true);
      const el = this.wrap, req = el.requestFullscreen || el.webkitRequestFullscreen;
      try { const p = req && req.call(el); if (p && p.catch) p.catch(() => {}); } catch (e) { /* maximized only */ }
    }
    exitFull() {
      this.setMax(false);
      if (this.fsElement()) {
        const ex = document.exitFullscreen || document.webkitExitFullscreen;
        try { const p = ex.call(document); if (p && p.catch) p.catch(() => {}); } catch (e) { /* ignore */ }
      }
    }

    /* ---------------------------------------------------------------- page sound */
    rustle() {
      if (!this.sound || REDUCED.matches || !this.dlg.open) return;      // never while closed
      const now = Date.now();
      if (now - (this.lastRustle || 0) < 100) return;                     // no bursts from fast flipping
      this.lastRustle = now;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        this.audio = this.audio || new Ctx();
        const a = this.audio, dur = 0.32, n = Math.floor(a.sampleRate * dur);
        const buf = a.createBuffer(1, n, a.sampleRate), d = buf.getChannelData(0);
        for (let i = 0; i < n; i++) { const t = i / n; d[i] = (Math.random() * 2 - 1) * Math.pow(1 - t, 2.2) * (0.6 + 0.4 * Math.sin(t * 22)); }
        const src = a.createBufferSource(); src.buffer = buf;
        const band = a.createBiquadFilter(); band.type = 'bandpass'; band.Q.value = 0.7;
        band.frequency.setValueAtTime(2600, a.currentTime); band.frequency.exponentialRampToValueAtTime(900, a.currentTime + dur);
        const gain = a.createGain(); gain.gain.setValueAtTime(0.0001, a.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.09, a.currentTime + 0.04); gain.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + dur);
        src.connect(band).connect(gain).connect(a.destination); src.start();
      } catch (e) { /* audio unavailable */ }
    }
    renderSound() {
      const b = this.act('sound');
      b.setAttribute('aria-pressed', String(this.sound));
      b.setAttribute('aria-label', this.sound ? 'Mute page sound' : 'Turn page sound on');
    }

    /* ---------------------------------------------------------------- pages and links */
    linkLayer(i) {
      const layer = document.createElement('div'); layer.className = 'bv-links';
      for (const l of this.links[i]) {
        const a = document.createElement('a');
        a.style.left = l.b[0] + '%'; a.style.top = l.b[1] + '%'; a.style.width = l.b[2] + '%'; a.style.height = l.b[3] + '%';
        if (l.u) {
          a.href = l.u; a.target = '_blank'; a.rel = 'noopener noreferrer';
          a.setAttribute('aria-label', 'Link: ' + l.u);
        } else {
          a.href = '#'; a.setAttribute('aria-label', 'Go to page ' + (l.p + 1));
          a.addEventListener('click', (e) => { e.preventDefault(); this.goTo(l.p); });
        }
        layer.appendChild(a);
      }
      return layer;
    }

    endCard(tag) {
      const end = document.createElement(tag); end.className = 'bv-end' + (tag === 'div' ? ' bv-page' : '');
      const catalog = this.dataset.kind === 'catalog';
      const h = document.createElement('p'); h.className = 'bv-end__title'; h.textContent = catalog ? 'End of catalog' : 'End of preview';
      const p = document.createElement('p'); p.className = 'bv-end__text';
      p.textContent = catalog ? 'Thank you for reading.' : `Thank you for reading this preview of ${this.dataset.title || 'this book'}.`;
      const b = document.createElement('button'); b.type = 'button'; b.className = 'bv-end__close'; b.textContent = 'Close preview';
      b.addEventListener('click', () => this.dlg.close());
      end.append(h, p, b);
      return end;
    }

    buildBook() {
      const holder = this.ref('book'), pages = [], imgs = [];
      this.pages.forEach((p, i) => {
        const el = document.createElement('div'); el.className = 'bv-page';
        el.dataset.density = i === 0 ? 'hard' : 'soft';
        const im = document.createElement('img'); im.className = 'bv-page__img'; im.alt = `Page ${i + 1} of ${this.total}`;
        // No decoding="async" here: inside the flipbook's 3D context the page can be painted before an async-decoded image is ready.
        // No src yet either: the read-ahead queue (warm) downloads and decodes each page before it is shown.
        im.draggable = false; im.dataset.sd = this.sized(p.s); im.dataset.hd = p.h; this.watchImage(im, i);
        el.append(im, this.linkLayer(i)); holder.appendChild(el); pages.push(el); imgs.push(im);
      });
      this.flipImgs = imgs; this.loads = [];
      const end = this.endCard('div'); end.dataset.density = 'hard'; holder.appendChild(end); pages.push(end);
      const w = 500, h = Math.round(w / this.ratio);
      const book = new window.St.PageFlip(holder, {
        width: w, height: h, size: 'stretch', minWidth: 200, maxWidth: 2400, minHeight: Math.round(200 / this.ratio), maxHeight: Math.round(2400 / this.ratio),
        showCover: true, usePortrait: true, mobileScrollSupport: false, clickEventForward: true,
        maxShadowOpacity: 0.35, flippingTime: REDUCED.matches ? 1 : 750, showPageCorners: true,
      });
      book.loadFromHTML(pages);
      this.gentleCorners(book);
      // The library also reports the current page after a resize (zoom, full screen); only a real change plays the sound.
      // The library also reports the page after every re-layout (zoom, resize, full screen), and reports the left page
      // of a spread where we may have asked for the right one. Only a change of the visible spread plays the sound.
      book.on('flip', (e) => {
        const before = this.spreadOf(this.state.current)[0];
        this.state.current = e.data;
        if (this.spreadOf(e.data)[0] !== before) this.rustle();
        this.state.aim = null;
        this.warm();
        if (this.state.zoom > 1) this.sharpenNear();
        this.sync();
      });
      book.on('changeOrientation', () => this.sync());
      this.state.book = book;
      this.layout();
    }

    /* ---------------------------------------------------------------- loading ahead of the reader
       Flipbook pages are hidden until a turn reveals them, so the browser's own lazy loading would start a page's
       download mid-turn: a blank page on a first visit. Instead pages load ahead of the reader: the current spread,
       the next three spreads and the previous one first, then (on a normal connection) the rest of the book in reading
       order, three at a time. Each image is downloaded and decoded before it goes on the page; until then the page shows
       a soft "Loading page" placeholder. Arrow / keyboard turns and jumps wait briefly for their pages (whenReady). */
    slowNet() {
      const c = navigator.connection;
      return !!(c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || '')));
    }
    want(i) {
      let r = this.loads[i];
      if (!r) { let res; const promise = new Promise((x) => { res = x; }); r = this.loads[i] = { i, promise, resolve: res, state: 'queued' }; }
      return r;
    }
    pageReady(i) {
      if (!this.flipImgs || i < 0 || i >= this.total) return Promise.resolve(true);
      const r = this.want(i); this.pump(); return r.promise;
    }
    whenReady(list, max = 4000) {
      return Promise.race([Promise.all(list.map((i) => this.pageReady(i))), new Promise((r) => setTimeout(r, max))]);
    }
    warm() {
      if (!this.flipImgs) return;
      const c = this.state.aim ?? this.state.current;
      for (let i = c - 2; i <= c + 6; i++) if (i >= 0 && i < this.total) this.want(i);
      if (!this.slowNet()) for (let i = 0; i < this.total; i++) this.want(i);
      this.pump();
    }
    pump() {
      const MAX = 3, c = this.state.aim ?? this.state.current;
      // The pages on screen (or about to be) load first and alone, so they get the whole connection.
      const visible = this.spreadOf(c).filter((i) => i < this.total);
      for (const i of visible) this.want(i);
      const visibleBusy = visible.some((i) => this.loads[i].state !== 'done');
      const rank = (i) => (i >= c ? i - c : (c - i) * 3);          // pages ahead first, then just behind
      // Those pages always start at once (a slot is never taken from them); reading ahead uses at most 2 at a time.
      for (const i of visible) if (this.loads[i].state === 'queued') this.fetchPage(this.loads[i], true);
      if (visibleBusy) return;
      let active = this.loads.filter((r) => r && r.state === 'loading').length;
      const queued = this.loads.filter((r) => r && r.state === 'queued').sort((a, b) => rank(a.i) - rank(b.i));
      for (const r of queued) { if (active >= MAX - 1) break; active++; this.fetchPage(r, false); }
    }
    fetchPage(r, urgent) {
      r.state = 'loading';
      const im = this.flipImgs[r.i], url = im.dataset.sd, pre = new Image();
      pre.fetchPriority = urgent ? 'high' : 'low';
      const done = (ok) => {
        r.state = 'done';
        if (ok) { if (!im.getAttribute('src')) im.setAttribute('src', url); im.parentElement.classList.add('is-loaded'); }
        else this.showMissing(im, r.i);                                    // failed: say so right away, no second request
        r.resolve(ok); this.pump();
      };
      pre.onload = () => { (pre.decode ? pre.decode() : Promise.resolve()).then(() => done(true), () => done(true)); };
      pre.onerror = () => done(false);
      pre.src = url;
    }
    // Zoomed: sharper images for the pages around the reader only (not the whole book).
    sharpenNear() {
      if (!this.flipImgs) return;
      const c = this.state.current, hd = this.state.zoom > 1;
      this.flipImgs.forEach((im, i) => {
        if (!im.getAttribute('src')) return;
        this.swapSrc(im, hd && i >= c - 2 && i <= c + 3 ? im.dataset.hd : im.dataset.sd);
      });
    }

    // Hovering near a corner shows a small, fixed peel that hints the page can turn. StPageFlip's default reacts in a
    // corner zone one fifth of the page diagonal wide and lets the fold follow the cursor deeper, which covers page
    // numbers and footer links. Adjusted on this book only (the vendored library is unmodified): small hover zone,
    // shallow peel that stays put. Dragging a corner and click-to-turn are unchanged.
    gentleCorners(book) {
      const fc = book.getFlipController && book.getFlipController();
      if (!fc || typeof fc.showCorner !== 'function' || typeof fc.isPointOnCorners !== 'function') return;
      const PEEL = 26;
      fc.isPointOnCorners = function (t) {
        const r = this.getBoundsRect(), s = Math.min(56, Math.max(24, r.pageWidth * 0.09)), p = this.render.convertToBook(t);
        return p.x > 0 && p.y > 0 && p.x < r.width && p.y < r.height && (p.x < s || p.x > r.width - s) && (p.y < s || p.y > r.height - s);
      };
      fc.showCorner = function (t) {
        if (!this.checkState('read', 'fold_corner')) return;
        const r = this.getBoundsRect(), w = r.pageWidth;
        if (!this.isPointOnCorners(t)) { this.setState('read'); this.render.finishAnimation(); this.stopMove(); return; }
        if (this.calc !== null) return;                    // keep the small peel; don't follow the cursor
        if (!this.start(t)) return;
        this.setState('fold_corner');
        this.calc.calc({ x: w - 1, y: 1 });
        const bottom = this.calc.getCorner() === 'bottom';
        this.animateFlippingTo({ x: w - 1, y: bottom ? r.height - 1 : 1 }, { x: w - PEEL, y: bottom ? r.height - PEEL : PEEL }, false, false);
      };
    }

    buildScroll() {
      const col = this.ref('col');
      if (col.childElementCount) return;
      this.pages.forEach((p, i) => {
        const fig = document.createElement('figure'); fig.className = 'bv-spage'; fig.dataset.page = String(i);
        const box = document.createElement('div'); box.className = 'bv-spage__box';
        const im = document.createElement('img'); im.loading = 'lazy'; im.decoding = 'async';
        im.alt = `Page ${i + 1} of ${this.total}`; im.dataset.sd = p.s; im.dataset.hd = p.h; this.watchImage(im, i); im.src = p.s;
        im.width = 1000; im.height = Math.round(1000 / this.ratio);
        box.append(im, this.linkLayer(i));
        const cap = document.createElement('figcaption'); cap.textContent = `Page ${i + 1} of ${this.total}`;
        fig.append(box, cap); col.appendChild(fig);
      });
      col.appendChild(this.endCard('section'));
      const io = new IntersectionObserver((entries) => {
        if (!this.dlg.open || this.state.mode !== 'scroll' || this.state.scrollLock) return;
        for (const en of entries) if (en.isIntersecting) { this.state.current = Number(en.target.dataset.page); this.sync(); }
      }, { root: this.ref('scroll'), threshold: 0.5 });
      col.querySelectorAll('.bv-spage').forEach((f) => io.observe(f));
    }

    /* One page at a time when the stage is tall (a spread would be small), a two-page spread otherwise.
       The library shows one page when the book is narrower than 2 x minWidth, so minWidth is the switch. */
    layout() {
      const book = this.state.book, flip = this.ref('flip');
      if (!book || this.state.mode !== 'flip' || !flip.clientWidth) return;
      const cs = getComputedStyle(flip);
      const w = flip.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const h = flip.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom);
      const single = w / Math.max(h, 1) < this.ratio * 1.45;
      flip.toggleAttribute('data-single', single);
      book.getSettings().minWidth = single ? 100000 : 200;
      requestAnimationFrame(() => { book.update(); this.sync(); });
    }

    /* ---------------------------------------------------------------- navigation */
    // Pages in the current mode: the flipbook adds the closing page after the last preview page.
    last() { return this.state.mode === 'flip' ? this.total : this.total - 1; }
    spreadOf(i) {
      const book = this.state.book, count = this.total + 1;
      if (this.state.mode === 'scroll' || !book || book.getOrientation() === 'portrait') return [i];
      if (i === 0) return [0];
      if (i === count - 1 && count % 2 === 0) return [i];
      const left = i % 2 === 1 ? i : i - 1;
      return [left, left + 1].filter((x) => x < count);
    }
    label(i) {
      const end = this.dataset.kind === 'catalog' ? 'End of catalog' : 'End of preview';
      const s = this.spreadOf(i).filter((x) => x < this.total);
      if (!s.length) return end;
      const pages = s.length === 2 ? `Pages ${s[0] + 1}–${s[1] + 1} of ${this.total}` : (s[0] === 0 ? 'Cover' : `Page ${s[0] + 1} of ${this.total}`);
      return this.spreadOf(i).includes(this.total) ? `${pages} · ${end}` : pages;
    }
    sync() {
      const i = this.state.current;
      this.ref('count').textContent = this.label(i);
      this.ref('range').value = String(Math.min(i, this.total - 1) + 1);
      this.act('prev').disabled = i <= 0;
      this.act('next').disabled = this.spreadOf(i).includes(this.last()) || i >= this.last();
    }
    goTo(i) {
      i = Math.min(Math.max(i, 0), this.last());
      if (this.state.mode === 'scroll') {
        const fig = this.ref('col').children[i];
        if (fig) fig.scrollIntoView({ behavior: REDUCED.matches ? 'auto' : 'smooth', block: 'start' });
        this.state.current = i; this.sync(); return;
      }
      const book = this.state.book;
      if (!book || i === this.state.current) return;
      this.state.aim = i; this.warm();
      this.whenReady([i - 1, i, i + 1]).then(() => {
        if (Math.abs(i - this.state.current) <= 2 && !REDUCED.matches) { if (i > this.state.current) book.flipNext(); else book.flipPrev(); return; }
        const before = this.spreadOf(this.state.current)[0];
        book.turnToPage(i);
        if (this.spreadOf(i)[0] !== before && this.spreadOf(this.state.current)[0] === before) this.rustle();   // library didn't report it
        this.state.current = book.getCurrentPageIndex ? book.getCurrentPageIndex() : i; this.state.aim = null;
        this.warm(); this.sync();
      });
    }
    step(dir) {
      const book = this.state.book;
      if (this.state.mode === 'scroll') return this.goTo(this.state.current + dir);
      if (!book) return;
      if (REDUCED.matches) { const s = this.spreadOf(this.state.current); return this.goTo(dir > 0 ? s[s.length - 1] + 1 : s[0] - 1); }
      // Rapid presses queue (up to 3): each turn waits for the previous one to finish, then for its own pages,
      // so a fast reader never turns onto a page that hasn't arrived yet.
      if ((this.pendingTurns || 0) >= 3) return;
      this.pendingTurns = (this.pendingTurns || 0) + 1;
      this.turnQueue = (this.turnQueue || Promise.resolve()).then(async () => {
        try {
          await this.settled();
          if (!this.dlg.open || this.state.mode !== 'flip') return;
          const s = this.spreadOf(this.state.current);
          if (dir > 0 ? s.includes(this.last()) : s[0] <= 0) return;
          const target = dir > 0 ? [s[s.length - 1] + 1, s[s.length - 1] + 2] : [s[0] - 1, s[0] - 2];
          this.state.aim = Math.max(0, Math.min(target[0], this.total - 1)); this.warm();   // its pages go first
          // If the pages need a moment (slow connection), the arrow pulses so the press visibly registered.
          const arrow = this.act(dir > 0 ? 'next' : 'prev');
          const busy = setTimeout(() => arrow.classList.add('is-waiting'), 250);
          await this.whenReady(target);
          clearTimeout(busy); arrow.classList.remove('is-waiting');
          if (dir > 0) book.flipNext(); else book.flipPrev();
          await this.settled();
        } finally { this.pendingTurns -= 1; }
      });
    }
    // Resolves when no page turn is running (the library is reading, or showing the corner peel).
    settled(max = 2500) {
      const fc = this.state.book && this.state.book.getFlipController && this.state.book.getFlipController();
      const t0 = Date.now();
      return new Promise((resolve) => {
        const check = () => {
          const st = fc && fc.state;
          if (!fc || st === 'read' || st === 'fold_corner' || Date.now() - t0 > max) resolve(); else setTimeout(check, 30);
        };
        setTimeout(check, 30);
      });
    }

    /* ---------------------------------------------------------------- modes and zoom */
    setMode(next) {
      // Opened at phone width and then widened (tablet rotated, window resized): load the flipbook now.
      if (next !== 'scroll' && !PHONE.matches && !(window.St && window.St.PageFlip)) {
        loadLibrary(this.dataset.lib).then(() => { if (this.dlg.open && !PHONE.matches) this.setMode(next); }).catch(() => {});
      }
      const canFlip = !PHONE.matches && window.St && window.St.PageFlip;
      const mode = canFlip ? (next === 'scroll' ? 'scroll' : 'flip') : 'scroll';
      this.state.mode = mode;
      this.querySelectorAll('[data-mode]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.mode === mode)));
      this.ref('flip').style.display = mode === 'flip' ? '' : 'none';
      this.ref('scroll').classList.toggle('is-on', mode === 'scroll');
      this.act('prev').hidden = this.act('next').hidden = mode !== 'flip';
      if (mode === 'flip' && !this.state.book) this.buildBook();
      if (mode === 'flip') this.warm();
      if (mode === 'scroll') {
        this.buildScroll();
        // Keep the reader's place: scroll to the current page before the page tracker resumes.
        const i = this.state.current;
        this.state.scrollLock = true;
        requestAnimationFrame(() => {
          const fig = this.ref('col').children[i]; if (fig) fig.scrollIntoView({ block: 'start' });
          setTimeout(() => { this.state.scrollLock = false; }, 400);
        });
      } else if (this.state.book) {
        this.state.book.turnToPage(this.state.current);
        this.layout();
      }
      this.setZoom(1); this.sync();
      if (!PHONE.matches) store.set('ci-viewer-mode', mode);
    }

    setZoom(z, anchor) {
      const st = this.state, flip = st.mode === 'flip';
      st.zoom = Math.round(Math.min(Math.max(z, MIN_Z), MAX_Z) * 100) / 100;
      this.ref('zoom').textContent = Math.round(st.zoom * 100) + '%';
      this.act('zoom-out').disabled = st.zoom <= MIN_Z; this.act('zoom-in').disabled = st.zoom >= MAX_Z;
      const stage = flip ? this.ref('flip') : this.ref('scroll');
      const r = stage.getBoundingClientRect();
      const ax = anchor ? anchor.x - r.left : stage.clientWidth / 2, ay = anchor ? anchor.y - r.top : stage.clientHeight / 2;
      const fx = (stage.scrollLeft + ax) / Math.max(stage.scrollWidth, 1), fy = (stage.scrollTop + ay) / Math.max(stage.scrollHeight, 1);
      if (flip) {
        stage.classList.toggle('is-zoomed', st.zoom > 1);
        this.ref('fit').style.setProperty('--z', String(st.zoom));
        this.sharpenNear();
      } else {
        const col = this.ref('col');
        col.style.width = `min(${Math.round(760 * st.zoom)}px, ${Math.round(100 * st.zoom)}%)`;
        if (st.zoom > 1) col.querySelectorAll('img').forEach((im) => { if (im.dataset.hd) this.swapSrc(im, im.dataset.hd); });
      }
      cancelAnimationFrame(st.zoomFrame);
      st.zoomFrame = requestAnimationFrame(() => {
        if (flip && st.book) st.book.update();
        if (st.zoom === 1 && flip) { stage.scrollLeft = 0; stage.scrollTop = 0; return; }
        stage.scrollLeft = fx * stage.scrollWidth - ax;
        stage.scrollTop = fy * stage.scrollHeight - ay;
      });
    }
    // A page image that fails to load: fall back from the sharper image to the normal one, and if that fails too,
    // show a short notice on the page instead of a blank white page.
    watchImage(im, i) {
      im.addEventListener('error', () => {
        const sd = im.dataset.sd;
        if (sd && im.getAttribute('src') !== sd) { im.setAttribute('src', sd); return; }
        this.showMissing(im, i);
      });
    }
    showMissing(im, i) {
      const box = im.parentElement;
      if (!box || box.querySelector('.bv-missing')) return;
      im.style.visibility = 'hidden';
      const note = document.createElement('p'); note.className = 'bv-missing'; note.setAttribute('role', 'note');
      note.textContent = `Page ${i + 1} couldn't be loaded. Please try again later.`;
      box.appendChild(note);
    }

    // Keep the current image on screen until the new one has downloaded, so zooming never shows a blank page.
    swapSrc(im, want) {
      if (!want || im.getAttribute('src') === want || im.dataset.pending === want) return;
      im.dataset.pending = want;
      const pre = new Image();
      pre.onload = () => { if (im.dataset.pending === want) { im.setAttribute('src', want); delete im.dataset.pending; } };
      pre.onerror = () => { if (im.dataset.pending === want) delete im.dataset.pending; };
      pre.src = want;
    }
    zoomStep(dir) {
      const z = this.state.zoom;
      this.setZoom(dir > 0 ? (ZOOMS.find((v) => v > z + 0.01) || MAX_Z) : ([...ZOOMS].reverse().find((v) => v < z - 0.01) || MIN_Z));
    }

    /* ---------------------------------------------------------------- controls and gestures */
    wireControls() {
      const st = this.state;
      this.act('close').addEventListener('click', () => this.dlg.close());
      this.dlg.addEventListener('close', () => { this.exitFull(); document.documentElement.style.overflow = ''; });
      // Esc leaves full screen first; a second Esc closes the viewer.
      this.dlg.addEventListener('cancel', (e) => { if (this.dlg.classList.contains('is-max')) { e.preventDefault(); this.exitFull(); } });
      for (const type of ['fullscreenchange', 'webkitfullscreenchange']) {
        document.addEventListener(type, () => { if (!this.fsElement() && this.dlg.classList.contains('is-max')) this.setMax(false); });
      }
      this.act('fullscreen').addEventListener('click', () => (this.dlg.classList.contains('is-max') ? this.exitFull() : this.enterFull()));
      this.act('sound').addEventListener('click', () => { this.sound = !this.sound; store.set('ci-viewer-sound', this.sound ? 'on' : 'off'); this.renderSound(); });
      this.act('zoom-in').addEventListener('click', () => this.zoomStep(1));
      this.act('zoom-out').addEventListener('click', () => this.zoomStep(-1));
      this.act('prev').addEventListener('click', () => this.step(-1));
      this.act('next').addEventListener('click', () => this.step(1));
      this.querySelectorAll('[data-mode]').forEach((b) => b.addEventListener('click', () => this.setMode(b.dataset.mode)));
      PHONE.addEventListener('change', () => { if (this.dlg.open) this.setMode(store.get('ci-viewer-mode') || 'flip'); });
      let resizeTimer = 0;
      window.addEventListener('resize', () => {
        if (!this.dlg.open) return;
        clearTimeout(resizeTimer); resizeTimer = setTimeout(() => this.layout(), 150);
      });
      this.dlg.addEventListener('keydown', (e) => {
        if (e.target.closest('input')) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        if (e.key === '+' || e.key === '=') this.zoomStep(1);
        if (e.key === '-') this.zoomStep(-1);
      });

      // Scrubber
      const range = this.ref('range'), scrub = this.ref('scrub'), bubble = this.ref('bubble');
      const showBubble = () => {
        const pct = (Number(range.value) - 1) / Math.max(this.total - 1, 1);
        bubble.textContent = 'Page ' + range.value;
        bubble.style.left = `calc(${pct * 100}% + ${(0.5 - pct) * 16}px)`;
      };
      range.addEventListener('input', () => {
        scrub.classList.add('is-active'); showBubble();
        if (this.state.mode === 'flip') { this.state.aim = Number(range.value) - 1; this.warm(); }
      });
      range.addEventListener('change', () => { scrub.classList.remove('is-active'); this.goTo(Number(range.value) - 1); });
      range.addEventListener('pointerup', () => scrub.classList.remove('is-active'));

      // Pan while zoomed: drag from anywhere (a link still opens on a click without movement), wheel / two-finger swipe.
      const flip = this.ref('flip');
      let down = false, moved = false, sx = 0, sy = 0, sl = 0, stp = 0;
      const touches = new Map();
      flip.addEventListener('pointerdown', (e) => {
        if (st.zoom === 1 || e.button !== 0 || (e.pointerType === 'touch' && touches.size > 1)) return;
        e.stopPropagation(); down = true; moved = false; sx = e.clientX; sy = e.clientY; sl = flip.scrollLeft; stp = flip.scrollTop;
      }, true);
      window.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (!moved && Math.hypot(dx, dy) < 4) return;
        if (!moved) { moved = true; flip.classList.add('is-dragging'); }
        e.preventDefault(); flip.scrollLeft = sl - dx; flip.scrollTop = stp - dy;
      }, { capture: true, passive: false });
      const endDrag = () => { if (!down) return; down = false; flip.classList.remove('is-dragging'); };
      window.addEventListener('pointerup', endDrag, true); window.addEventListener('pointercancel', endDrag, true);
      for (const type of ['mousedown', 'mousemove', 'mouseup', 'touchstart', 'touchmove', 'touchend']) {
        flip.addEventListener(type, (e) => { if (st.zoom > 1) e.stopPropagation(); }, true);  // keep the flipbook's own drag out while zoomed
      }
      flip.addEventListener('click', (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      flip.addEventListener('dragstart', (e) => e.preventDefault());
      // No double-click zoom: a click turns the page, so fast page turning would read as a double-click and zoom.
      // Zoom stays on pinch, the -/+ buttons and the + / - keys.
      flip.addEventListener('wheel', (e) => {
        if (st.zoom === 1 || e.ctrlKey) return;
        e.preventDefault();
        const k = e.deltaMode === 1 ? 32 : e.deltaMode === 2 ? flip.clientHeight : 1;
        flip.scrollLeft += e.deltaX * k; flip.scrollTop += e.deltaY * k;
      }, { passive: false });

      // Pinch zooms the pages, not the browser.
      const stage = this.querySelector('.bv-stage');
      stage.addEventListener('wheel', (e) => {
        if (!e.ctrlKey) return;
        e.preventDefault();
        this.setZoom(st.zoom * Math.exp(-e.deltaY * 0.01), { x: e.clientX, y: e.clientY });
      }, { passive: false });
      let gestureStart = 1;
      stage.addEventListener('gesturestart', (e) => { e.preventDefault(); gestureStart = st.zoom; }, { passive: false });
      stage.addEventListener('gesturechange', (e) => { e.preventDefault(); this.setZoom(gestureStart * e.scale, { x: e.clientX, y: e.clientY }); }, { passive: false });
      stage.addEventListener('gestureend', (e) => e.preventDefault(), { passive: false });
      let pinch = null;
      const dist = () => { const [a, b] = [...touches.values()]; return Math.hypot(a.x - b.x, a.y - b.y) || 1; };
      const mid = () => { const [a, b] = [...touches.values()]; return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }; };
      stage.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'touch') return;
        touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (touches.size === 2) { pinch = { d: dist(), z: st.zoom }; down = false; e.stopPropagation(); }
      }, true);
      stage.addEventListener('pointermove', (e) => {
        if (!touches.has(e.pointerId)) return;
        touches.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pinch && touches.size === 2) { e.stopPropagation(); this.setZoom(pinch.z * dist() / pinch.d, mid()); }
      }, true);
      const lift = (e) => { touches.delete(e.pointerId); if (touches.size < 2) pinch = null; };
      stage.addEventListener('pointerup', lift, true); stage.addEventListener('pointercancel', lift, true);
    }
  }

  customElements.define('book-viewer', BookViewer);
})();
